import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'erp', label: 'ERP Implementation' },
  { value: 'cloud', label: 'Cloud Migration' },
  { value: 'custom', label: 'Custom Applications' },
  { value: 'managed', label: 'Managed Services' }
];

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        setCaseStudies(response.data);
        setFilteredStudies(response.data);
      } catch (error) {
        console.error('Error fetching case studies:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredStudies(caseStudies);
    } else {
      setFilteredStudies(caseStudies.filter(cs => cs.category === selectedCategory));
    }
  }, [selectedCategory, caseStudies]);

  return (
    <div className="pt-20" data-testid="case-studies-page">
      <SEO 
        title="Case Studies"
        description="Explore our Oracle transformation success stories. See how we've helped leading enterprises achieve exceptional results through ERP implementations and cloud migrations."
        keywords="Oracle Case Studies, ERP Success Stories, Cloud Migration Results, Enterprise Transformation, Oracle Implementation"
      />
      {/* Hero Section */}
      <section className="section-dark py-12 md:py-20 lg:py-32">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center lg:text-left lg:mx-0">
            <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Case Studies</span>
            <h1 className="heading-xl text-white mb-4 md:mb-6 font-heading">
              Transformation
              <span className="block text-oracle-red">Success Stories</span>
            </h1>
            <p className="text-slate-300 font-body text-base md:text-lg lg:text-xl leading-relaxed">
              Explore how we've helped leading global enterprises achieve exceptional results through Oracle solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="section-light py-6 md:py-8 border-b border-slate-200">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-4">
            <p className="font-body text-slate-600 text-sm">
              Showing {filteredStudies.length} case {filteredStudies.length === 1 ? 'study' : 'studies'}
            </p>
            <div className="flex items-center space-x-2 md:space-x-3 w-full sm:w-auto">
              <Filter className="w-4 h-4 md:w-5 md:h-5 text-slate-400" />
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-[180px] md:w-[200px] font-body text-sm" data-testid="category-filter">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="section-light py-12 md:py-20 lg:py-32" data-testid="case-studies-grid">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="aspect-video bg-slate-200" />
                  <CardContent className="p-4 md:p-8">
                    <div className="h-4 bg-slate-200 rounded w-20 mb-4" />
                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-4" />
                    <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredStudies.length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <p className="font-body text-slate-600 text-base md:text-lg">
                No case studies found for this category.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setSelectedCategory('all')}
              >
                View All Case Studies
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
              {filteredStudies.map((study, index) => (
                <Link 
                  key={study.id} 
                  to={`/case-studies/${study.id}`}
                  className="group"
                >
                  <Card 
                    className="card-enterprise card-hover-lift overflow-hidden h-full"
                    data-testid={`case-study-card-${index}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={study.image_url || 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800'}
                        alt={study.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-4 md:p-6 lg:p-8">
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <span className="eyebrow text-xs">{study.category.toUpperCase()}</span>
                        <span className="font-body text-[10px] md:text-xs text-slate-500">{study.industry}</span>
                      </div>
                      <h3 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-3 md:mb-4 group-hover:text-oracle-red transition-colors">
                        {study.title}
                      </h3>
                      <p className="font-body text-slate-600 text-xs md:text-sm mb-4 md:mb-6 line-clamp-2">
                        {study.challenge}
                      </p>
                      
                      {/* Metrics */}
                      <div className="flex flex-wrap gap-2 md:gap-4 mb-4 md:mb-6">
                        {study.metrics?.slice(0, 3).map((metric, i) => (
                          <div key={i} className="bg-slate-50 px-3 md:px-4 py-1.5 md:py-2 rounded-sm">
                            <div className="font-heading text-base md:text-lg font-bold text-oracle-red">
                              {metric.value}
                            </div>
                            <div className="font-body text-[10px] md:text-xs text-slate-500">
                              {metric.label}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex items-center text-oracle-red font-body text-xs md:text-sm font-medium group-hover:translate-x-2 transition-transform">
                        Read Full Case Study <ArrowRight className="ml-2 w-3 h-3 md:w-4 md:h-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark py-12 md:py-20 lg:py-32" data-testid="case-studies-cta">
        <div className="container-custom text-center">
          <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Your Success Story Starts Here</span>
          <h2 className="heading-lg text-white mb-4 md:mb-6 font-heading max-w-2xl mx-auto">
            Ready to Achieve Similar Results?
          </h2>
          <p className="text-slate-300 font-body text-sm md:text-base lg:text-lg mb-6 md:mb-8 max-w-xl mx-auto">
            Let's discuss how our Oracle expertise can transform your enterprise operations.
          </p>
          <Link to="/contact" className="inline-block">
            <Button 
              size="lg" 
              className="bg-oracle-red hover:bg-oracle-red-dark text-white font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
              data-testid="case-studies-schedule-btn"
            >
              Schedule Consultation
              <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
